import { HttpClient, HttpHeaders, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, from, throwError } from 'rxjs';
import { switchMap, catchError, map } from 'rxjs/operators';
import { CognitoService } from 'src/app/auth/services/cognito.service';
import { environment } from 'src/environments/environment';

export interface VitaHealth {
  status: 'ok' | 'error';
  environment: string;
  latencyMs: number;
  timestamp: string;
  message?: string;
  vitaErrorCode?: number | null;
}

export interface VitaWalletItem {
  uuid: string;
  type: string;
  attributes: {
    token: string;
    created_at: string;
    balances: Record<string, number>;
    is_master: boolean;
  };
}

export interface VitaWalletListResponse {
  wallets: VitaWalletItem[];
  total: number;
  count: number;
}

export interface VitaCountry {
  id: number;
  code: string;
  name: string;
  currency: string;
  isActive: boolean;
  supportsPayin: boolean;
  supportsPayout: boolean;
  displayOrder: number;
  flagIcon: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface VitaPricesAttributes {
  valid_until?: string;
  business_days_of_payment?: Record<string, number>;
  fixed_cost?: Record<string, string>;
  fixed_cost_usd?: Record<string, string>;
  min_amount?: Record<string, string>;
  [key: string]: any; // {currency}_sell
}

export interface VitaPricesBlock {
  prices?: {
    type?: string;
    attributes?: VitaPricesAttributes;
  };
}

export interface VitaPaymentMethod {
  payment_method: string;
  sell_price: string;
  fixed_cost: number;
  spread: number;
  spread_type: string;
}

export interface VitaPayinEntry {
  destination_currency: string;
  source_currency: string;
  payment_methods: VitaPaymentMethod[];
}

/**
 * Variante del payload público de Vita (`prices_quote`).
 *
 * Es la tasa que un cliente final vería si fuera directo a vitawallet.io.
 * Llega via el proxy backend Aguacate (`/vita-wallet/public-prices`) — Vita
 * bloquea el endpoint desde browser pero acepta calls server-to-server con
 * headers Origin/Referer del subdominio tracking.
 */
export interface VitaPublicQuoteVariant {
  usd_buy: number;
  usd_sell: number;
  usd_buy_send: number;
  usd_sell_send: number;
  usd_min: string | number;
  usd_fixed_cost: string | number;
  usd_fixed_cost_send: string | number;
  usd_fixed_cost_transfer: string | number;
  usd_fixed_cost_vita_card: string | number;
  usd_is_apply_fixed_cost_in_sents?: boolean;
  usd_is_apply_fixed_cost_in_transfers?: boolean;
  usd_is_apply_fixed_cost_in_withdrawal?: boolean;
  usd_is_apply_fixed_cost_in_vita_card?: boolean;
  usd_precision?: number;
}

export interface VitaPublicQuoteData {
  valid_until: string;
  fiat: Record<string, VitaPublicQuoteVariant>;
}

/**
 * Envelope del endpoint `GET /vita-wallet/public-prices` — el backend
 * agrega `fetchedAt` (timestamp de fetch desde Vita) además del payload
 * crudo de Vita en `data`.
 */
export interface VitaPublicQuoteResponse {
  fetchedAt: string;
  data: VitaPublicQuoteData;
}

export interface VitaPricesResponse {
  payins?: Record<string, VitaPayinEntry>;
  withdrawal?: VitaPricesBlock;
  vita_sent?: VitaPricesBlock;
  [currency: string]: any;
}

export interface VitaAvailableVariant {
  code: string;
  label: string;
  destinationCurrency: string;
  sellPriceUsd: number;
  fixedCostUsd: number;
  minAmountUsd: number;
  businessDays: number;
  validUntil: string | null;
}

export interface VitaAvailableCountry {
  code: string;
  name: string;
  currency: string;
  flagIcon: string | null;
  variants: VitaAvailableVariant[];
  inDb: boolean;
  dbCountryId?: number;
}

export interface VitaCreateCountryPayload {
  code: string;
  name?: string;
  currency?: string;
  flagIcon?: string | null;
  displayOrder?: number;
  supportsPayin?: boolean;
  supportsPayout?: boolean;
}

export interface VitaUpdateCountryPayload {
  name?: string;
  currency?: string;
  flagIcon?: string | null;
  displayOrder?: number;
  supportsPayin?: boolean;
  supportsPayout?: boolean;
}

export interface VitaDiagnostics {
  environment: string;
  baseUrl: string;
  xLoginFingerprint: string | null;
  prices: {
    reachable: boolean;
    validUntil: string | null;
    variantCount: number;
    variants: string[];
    hasCO: boolean;
    hasCOCOP: boolean;
    hasCOBREB: boolean;
  } | null;
  rules: {
    reachable: boolean;
    variantCount: number;
    variants: string[];
  } | null;
  errors: string[];
  fetchedAt: string;
}

// ─── Conversión interna en la wallet master (exchange) ───

export interface VitaCryptoPricesResponse {
  prices: Record<string, Record<string, number>>;
  valid_until: string;
}

export interface VitaConversionPlan {
  currencySent: string;
  currencyReceived: string;
  amountSent: number;
  rateSell: number;
  rateMid: number | null;
  spreadPct: number | null;
  amountReceived: number;
  effectiveRate: number;
  validUntil: string;
}

export interface VitaConversionResult {
  status: 'completed';
  order: string;
  vitaId: string;
  currencySent: string;
  currencyReceived: string;
  amountSent: number;
  amountReceived: number;
  rate: number;
  vitaStatus: string;
}

// ─── Movimientos de la cuenta Vita (transactions / payins / payouts) ───

export interface VitaTxWalletInfo {
  uuid: string;
  token: string;
  is_master: boolean;
}

export interface VitaTxAttributes {
  status: string;
  currency: string;
  currency_iso_code: string;
  amount: string;
  total: string;
  fee_value: string;
  total_fee: string;
  created_at: string;
  recipient_email: string | null;
  order: string;
  category: string;
  recipient_wallet: VitaTxWalletInfo | null;
  sender_wallet: VitaTxWalletInfo | null;
  included: { withdrawal?: any } | null;
  vita_transaction_id: string;
  tracking_url: string | null;
  [key: string]: any;
}

export interface VitaTransactionItem {
  id: string;
  type: string;
  attributes: VitaTxAttributes;
}

/** Filtros del endpoint `get_all_transactions` del backend. */
export interface VitaTxQuery {
  page?: number;
  count?: number;
  status?: string;
  transactions_type?: string;
  currency?: string;
  source?: string;
  sort?: string;
  start_date?: string;
  end_date?: string;
  min_amount?: number;
  max_amount?: number;
}

/** Respuesta normalizada (Vita devuelve {transactions,total,count}). */
export interface VitaTransactionsResult {
  items: VitaTransactionItem[];
  total: number;
  count: number;
}

export interface CountriesQuery {
  page?: number;
  limit?: number;
  search?: string;
  isActive?: 'true' | 'false';
  sortBy?: 'displayOrder' | 'name' | 'code' | 'createdAt';
  sortDir?: 'ASC' | 'DESC';
}

@Injectable({
  providedIn: 'root',
})
export class VitaWalletService {
  private baseUrl = environment.apiNest;

  constructor(
    private http: HttpClient,
    private cognitoService: CognitoService,
  ) {}

  private async getHeaders(): Promise<HttpHeaders> {
    let session = await this.cognitoService.getSession();
    let token = session?.accessToken?.jwtToken;

    // Si el token cacheado está por expirar, invalidar cache y pedir fresco
    if (token && this.isJwtNearExpiry(token, 60)) {
      if (typeof (this.cognitoService as any).clearCache === 'function') {
        (this.cognitoService as any).clearCache();
      }
      session = await this.cognitoService.getSession();
      token = session?.accessToken?.jwtToken;
    }

    if (!token) {
      throw new Error('No se pudo obtener el token de sesión');
    }
    return new HttpHeaders()
      .set('Content-Type', 'application/json')
      .set('Authorization', `Bearer ${token.trim()}`);
  }

  /** Decodifica el JWT y chequea si expira en menos de `thresholdSec`. */
  private isJwtNearExpiry(jwt: string, thresholdSec: number): boolean {
    try {
      const parts = jwt.split('.');
      if (parts.length !== 3) return false;
      const payload = JSON.parse(
        atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')),
      );
      if (!payload?.exp) return false;
      const nowSec = Math.floor(Date.now() / 1000);
      return payload.exp - nowSec < thresholdSec;
    } catch {
      return false;
    }
  }

  private handleError(error: HttpErrorResponse) {
    // 401/403 en polling silencioso: se loguea en consola pero no rompe UI
    if (error.status === 401 || error.status === 403) {
      console.warn(
        `[VitaWalletService] ${error.status} ${error.url} — token/guard transiente, retry en el próximo tick`,
      );
      return throwError(() => ({
        status: error.status,
        transient: true,
        message: `Autenticación en curso (${error.status})`,
      }));
    }
    let errorMessage = 'Ha ocurrido un error al procesar el envío';
    if (error.error instanceof ErrorEvent) {
      errorMessage = `Error: ${error.error.message}`;
    } else {
      errorMessage = error.error?.message || `Error del servidor: ${error.status}`;
    }
    return throwError(() => errorMessage);
  }

  private toParams(query: Record<string, any> | undefined): HttpParams {
    let params = new HttpParams();
    if (!query) return params;
    for (const [k, v] of Object.entries(query)) {
      if (v === undefined || v === null || v === '') continue;
      params = params.set(k, String(v));
    }
    return params;
  }

  // --- Health + wallets ---

  getHealth(): Observable<VitaHealth> {
    const url = `${this.baseUrl}health/vita`;
    return this.http.get<VitaHealth>(url).pipe(catchError(this.handleError));
  }

  getPrices(force = false): Observable<VitaPricesResponse> {
    const url = `${this.baseUrl}vita-wallet/prices`;
    const params = force ? new HttpParams().set('force', 'true') : new HttpParams();
    return from(this.getHeaders()).pipe(
      switchMap((headers) =>
        this.http.get<VitaPricesResponse>(url, { headers, params }),
      ),
      catchError(this.handleError),
    );
  }

  /**
   * Cotización pública de Vita Wallet vía proxy backend Aguacate.
   *
   * El endpoint público de Vita (`api.vitawallet.io/api/prices_quote`)
   * rechaza calls directas del browser (CORS + 401). El backend Aguacate
   * resuelve esto agregando headers `Origin/Referer` de tracking.vitawallet.io
   * en una llamada server-to-server, cacheando in-memory por `valid_until`.
   *
   * Se usa como **referencia competitiva** en el configurador de tramos:
   * la tasa que el cliente recibiría si fuera directo a Vita. El gap
   * contra la tasa B2B ([[getPrices]]) define la utilidad de Aguacate.
   */
  getPublicQuote(): Observable<VitaPublicQuoteResponse> {
    const url = `${this.baseUrl}vita-wallet/public-prices`;
    return from(this.getHeaders()).pipe(
      switchMap((headers) =>
        this.http.get<VitaPublicQuoteResponse>(url, { headers }),
      ),
      catchError(this.handleError),
    );
  }

  listWallets(page = 1, count = 20): Observable<VitaWalletListResponse> {
    const url = `${this.baseUrl}vita-wallet/wallets`;
    const params = new HttpParams()
      .set('page', String(page))
      .set('count', String(count));
    return from(this.getHeaders()).pipe(
      switchMap((headers) =>
        this.http.get<VitaWalletListResponse>(url, { headers, params }),
      ),
      catchError(this.handleError),
    );
  }

  // --- Conversión interna (exchange en wallet master) ---

  /** Precios cripto de Vita (base del cálculo de conversión). */
  getCryptoPrices(force = false): Observable<VitaCryptoPricesResponse> {
    const url = `${this.baseUrl}vita-wallet/exchange/crypto-prices`;
    const params = force ? new HttpParams().set('force', 'true') : new HttpParams();
    return from(this.getHeaders()).pipe(
      switchMap((headers) =>
        this.http.get<VitaCryptoPricesResponse>(url, { headers, params }),
      ),
      catchError(this.handleError),
    );
  }

  /** Cotiza (sin ejecutar) una conversión Vita, ej. CLP→USDT o USDT→USD. */
  quoteConversion(body: {
    currency_sent: string;
    currency_received: string;
    amount_sent: number;
  }): Observable<VitaConversionPlan> {
    const url = `${this.baseUrl}vita-wallet/exchange/convert/quote`;
    return from(this.getHeaders()).pipe(
      switchMap((headers) =>
        this.http.post<VitaConversionPlan>(url, body, { headers }),
      ),
      catchError(this.handleError),
    );
  }

  /** Ejecuta la conversión en la wallet master. `order` = clave idempotente. */
  convertCurrency(body: {
    currency_sent: string;
    currency_received: string;
    amount_sent: number;
    order: string;
  }): Observable<VitaConversionResult> {
    const url = `${this.baseUrl}vita-wallet/exchange/convert`;
    return from(this.getHeaders()).pipe(
      switchMap((headers) =>
        this.http.post<VitaConversionResult>(url, body, { headers }),
      ),
      catchError(this.handleError),
    );
  }

  // --- Movimientos de la cuenta Vita (read-only) ---

  /**
   * Lista todos los movimientos de la cuenta Vita (payins, payouts,
   * exchanges, etc.) vía el endpoint existente `transactions/all`
   * (`get_all_transactions` de Vita). Normaliza el envelope a
   * {items, total, count} — Vita devuelve {transactions, total, count}.
   */
  listVitaTransactions(query: VitaTxQuery): Observable<VitaTransactionsResult> {
    const url = `${this.baseUrl}vita-wallet/transactions/all`;
    const params = this.toParams(query as Record<string, any>);
    return from(this.getHeaders()).pipe(
      switchMap((headers) =>
        this.http.get<any>(url, { headers, params }),
      ),
      map((res) => ({
        items: res?.transactions ?? res?.data ?? [],
        total: res?.total ?? res?.pagination?.total_count ?? 0,
        count: res?.count ?? res?.pagination?.current_page_count ?? 0,
      })),
      catchError(this.handleError),
    );
  }

  // --- Catalog: countries ---

  listCountries(query?: CountriesQuery): Observable<PaginatedResponse<VitaCountry>> {
    const url = `${this.baseUrl}vita-wallet/catalog/countries`;
    const params = this.toParams(query);
    return from(this.getHeaders()).pipe(
      switchMap((headers) =>
        this.http.get<PaginatedResponse<VitaCountry>>(url, { headers, params }),
      ),
      catchError(this.handleError),
    );
  }

  // --- Catálogo live de Vita (read-only) ---
  // listAvailableCountries: qué publica Vita ahora mismo, con flag inDb.
  // getFullVitaCatalog: alias semántico para el simulador admin.

  listAvailableCountries(): Observable<VitaAvailableCountry[]> {
    const url = `${this.baseUrl}vita-wallet/catalog/countries/available`;
    return from(this.getHeaders()).pipe(
      switchMap((headers) =>
        this.http.get<VitaAvailableCountry[]>(url, { headers }),
      ),
      catchError(this.handleError),
    );
  }

  getFullVitaCatalog(): Observable<VitaAvailableCountry[]> {
    const url = `${this.baseUrl}vita-wallet/catalog/full-vita-catalog`;
    return from(this.getHeaders()).pipe(
      switchMap((headers) =>
        this.http.get<VitaAvailableCountry[]>(url, { headers }),
      ),
      catchError(this.handleError),
    );
  }

  // --- CRUD whitelist de países ---

  createCountry(dto: VitaCreateCountryPayload): Observable<VitaCountry> {
    const url = `${this.baseUrl}vita-wallet/catalog/countries`;
    return from(this.getHeaders()).pipe(
      switchMap((headers) =>
        this.http.post<VitaCountry>(url, dto, { headers }),
      ),
      catchError(this.handleError),
    );
  }

  updateCountry(
    id: number,
    dto: VitaUpdateCountryPayload,
  ): Observable<VitaCountry> {
    const url = `${this.baseUrl}vita-wallet/catalog/countries/${id}`;
    return from(this.getHeaders()).pipe(
      switchMap((headers) =>
        this.http.put<VitaCountry>(url, dto, { headers }),
      ),
      catchError(this.handleError),
    );
  }

  /**
   * Togglea `is_active` del país. Si se pasa `isActive` lo setea explícito;
   * si no, invierte el valor actual. **No hay eliminación** — una vez
   * agregado, el país se desactiva con este endpoint y queda en DB.
   */
  toggleCountryActive(
    id: number,
    isActive?: boolean,
  ): Observable<VitaCountry> {
    const url = `${this.baseUrl}vita-wallet/catalog/countries/${id}/toggle`;
    const body = typeof isActive === 'boolean' ? { isActive } : {};
    return from(this.getHeaders()).pipe(
      switchMap((headers) => this.http.post<VitaCountry>(url, body, { headers })),
      catchError(this.handleError),
    );
  }

  // --- Service status (VITA_SERVICIO toggle) ---

  getServiceStatus(): Observable<{ enabled: boolean }> {
    // Cache-buster: el home decide la visibilidad del botón "otros destinos"
    // con esta respuesta. Una entrada de caché corrupta en el browser hace que
    // el 304 de revalidación falle (ERR_CONTENT_DECODING) y el botón quede
    // oculto (incidente prod 2026-06-04). El `ts` garantiza un 200 fresco y
    // el backend además responde `Cache-Control: no-store`.
    const url = `${this.baseUrl}vita-wallet/catalog/service-status?ts=${Date.now()}`;
    return from(this.getHeaders()).pipe(
      switchMap((headers) =>
        this.http.get<{ enabled: boolean }>(url, { headers }),
      ),
      catchError(this.handleError),
    );
  }

  toggleServiceStatus(enabled?: boolean): Observable<{ enabled: boolean }> {
    const url = `${this.baseUrl}vita-wallet/catalog/service-status/toggle`;
    const body = typeof enabled === 'boolean' ? { enabled } : {};
    return from(this.getHeaders()).pipe(
      switchMap((headers) =>
        this.http.post<{ enabled: boolean }>(url, body, { headers }),
      ),
      catchError(this.handleError),
    );
  }

  // --- Diagnostics (read-only snapshot: ambiente + variantes reales de Vita) ---

  getDiagnostics(): Observable<VitaDiagnostics> {
    const url = `${this.baseUrl}vita-wallet/catalog/diagnostics`;
    return from(this.getHeaders()).pipe(
      switchMap((headers) =>
        this.http.get<VitaDiagnostics>(url, { headers }),
      ),
      catchError(this.handleError),
    );
  }

}
